const LIMA_TIMEZONE = 'America/Lima';
const BYTES_PER_GB = 1_000_000_000;
const BYTES_PER_TB = 1_000_000_000_000;

export function bpsToMbps(bps: number | null | undefined): number | null {
  if (bps === null || bps === undefined || Number.isNaN(bps)) return null;
  return bps / 1_000_000;
}

export function roundOrNull(value: number | null, decimals = 2): number | null {
  if (value === null) return null;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function formatLimaDateTime(isoOrDate: string): string {
  const date = new Date(isoOrDate);
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: LIMA_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatLimaMonthLabel(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-').map(Number);
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, 1, 12));
  const label = new Intl.DateTimeFormat('es-PE', {
    timeZone: LIMA_TIMEZONE,
    month: 'long',
    year: 'numeric',
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatLimaShortDate(yyyyMmDd: string): string {
  const [year, month, day] = yyyyMmDd.split('-').map(Number);
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1, 12));
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: LIMA_TIMEZONE,
    weekday: 'short',
    day: '2-digit',
  }).format(date);
}

export function currentLimaMonth(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: LIMA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  return `${year}-${month}`;
}

export function shiftYearMonth(yyyyMm: string, months: number): string {
  const [year, month] = yyyyMm.split('-').map(Number);
  const total = year * 12 + (month - 1) + months;
  const newYear = Math.floor(total / 12);
  const newMonth = (total % 12) + 1;
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
}

export function currentLimaDate(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: LIMA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
}

export function shiftDate(yyyyMmDd: string, days: number): string {
  const [year, month, day] = yyyyMmDd.split('-').map(Number);
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, (day ?? 1) + days, 12));
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC' }).format(date);
}

/** Último día Lima cerrado (ayer). El agregado daily no materializa hoy. */
export function lastClosedLimaDate(): string {
  return shiftDate(currentLimaDate(), -1);
}

export function dailyClosedRange(days: 7 | 30 | 90): { from: string; to: string } {
  const to = lastClosedLimaDate();
  return { from: shiftDate(to, -(days - 1)), to };
}

export function monthlyClosedRange(): { from: string; to: string } {
  const to = currentLimaMonth();
  return { from: shiftYearMonth(to, -11), to };
}

export type SiVolumeUnit = 'GB' | 'TB';

export function formatSiVolume(bytes: number | null | undefined): { value: number; unit: SiVolumeUnit } | null {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
    return null;
  }
  if (bytes >= BYTES_PER_TB) {
    return { value: roundOrNull(bytes / BYTES_PER_TB, 1) as number, unit: 'TB' };
  }
  return { value: roundOrNull(bytes / BYTES_PER_GB, 1) as number, unit: 'GB' };
}

export function formatSiVolumeLabel(bytes: number | null | undefined): string {
  const formatted = formatSiVolume(bytes);
  if (!formatted) return '—';
  return `${formatted.value} ${formatted.unit}`;
}

export function resolveSiVolumeUnit(values: Array<number | null | undefined>): SiVolumeUnit {
  const max = values.reduce<number>((acc, value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return acc;
    return Math.max(acc, value);
  }, 0);
  return max >= BYTES_PER_TB ? 'TB' : 'GB';
}

export function bytesToSiUnit(bytes: number | null | undefined, unit: SiVolumeUnit): number | null {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return null;
  const divisor = unit === 'TB' ? BYTES_PER_TB : BYTES_PER_GB;
  return roundOrNull(bytes / divisor, 1);
}
